import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL || "";

const fmt = (n) =>
  n == null ? "—" : new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n);

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return s;
  }
};

export default function ClientSignOff() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ signer_name: "", signer_email: "", signer_note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/sign-off/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `Error ${res.status}`);
        }
        return res.json();
      })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (!form.signer_name.trim() || !form.signer_email.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/sign-off/${token}/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "Sign-off failed");
      setResult(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Ambient void canvas ──────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#030712] text-white flex flex-col">
      {/* Ambient glow */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute top-[-10%] left-[30%] w-[600px] h-[600px] rounded-full bg-[#00F5D4]/4 blur-[120px]" />
        <div className="absolute bottom-[-5%] right-[20%] w-[400px] h-[400px] rounded-full bg-[#00F5D4]/3 blur-[100px]" />
      </div>

      {/* Header */}
      <header className="relative border-b border-[#1F2937] bg-[#030712]/80 backdrop-blur px-6 py-4 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-[#00F5D4]/10 border border-[#00F5D4]/20 flex items-center justify-center">
          <svg className="w-4 h-4 text-[#00F5D4]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#00F5D4] font-mono">
            {data?.workspace_name || "CombinedWork"}
          </div>
          <div className="text-xs text-[#6B7280]">Milestone Sign-Off Portal</div>
        </div>
      </header>

      {/* Body */}
      <main className="relative flex-1 flex items-start justify-center px-4 py-12">
        <div className="w-full max-w-lg space-y-6">

          {loading && (
            <div className="text-center text-[#6B7280] text-sm py-16">Loading…</div>
          )}

          {!loading && error && (
            <div className="rounded-xl border border-[#FF007A]/30 bg-[#FF007A]/5 p-6 text-center">
              <div className="text-[#FF007A] text-sm font-medium mb-1">Unable to load sign-off</div>
              <div className="text-[#9CA3AF] text-xs">{error}</div>
            </div>
          )}

          {!loading && !error && result && (
            <div className="rounded-xl border border-[#00F5D4]/30 bg-[#00F5D4]/5 p-8 text-center space-y-4">
              <div className="w-14 h-14 rounded-full bg-[#00F5D4]/10 border border-[#00F5D4]/30 flex items-center justify-center mx-auto shadow-glow-teal">
                <svg className="w-7 h-7 text-[#00F5D4]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-white">Sign-off recorded</h2>
              <p className="text-[#9CA3AF] text-sm">
                Your approval has been securely recorded on{" "}
                {fmtDate(result.confirmed_at)}.
                {result.invoice_id && " An invoice has been queued for review."}
              </p>
              <p className="text-[#6B7280] text-xs mt-2">
                Reference: <span className="font-mono text-[#00F5D4]/80">{result.signoff_id}</span>
              </p>
            </div>
          )}

          {!loading && !error && data && !result && (
            <>
              {/* Status banner for already-confirmed or expired */}
              {data.status === "confirmed" && (
                <div className="rounded-xl border border-[#00F5D4]/20 bg-[#00F5D4]/5 px-4 py-3 text-sm text-[#00F5D4] text-center">
                  This milestone was already approved by {data.signer_name} on {fmtDate(data.confirmed_at)}.
                </div>
              )}
              {data.status === "expired" && (
                <div className="rounded-xl border border-[#FF007A]/20 bg-[#FF007A]/5 px-4 py-3 text-sm text-[#FF007A] text-center">
                  This sign-off link has expired. Please contact the project team to request a new one.
                </div>
              )}

              {/* Milestone card */}
              <div className="rounded-xl border border-[#1F2937] bg-[#111827] p-6 space-y-4 shadow-glow-teal">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-[#00F5D4] font-mono">
                    Milestone
                  </span>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border font-mono ${
                    data.milestone?.status === "done"
                      ? "border-[#00F5D4]/30 text-[#00F5D4] bg-[#00F5D4]/8"
                      : "border-[#F59E0B]/30 text-[#F59E0B] bg-[#F59E0B]/8"
                  }`}>
                    {data.milestone?.status || "in progress"}
                  </span>
                </div>

                <h1 className="text-xl font-semibold text-white leading-tight">
                  {data.milestone?.title || "Milestone"}
                </h1>

                {data.milestone?.description && (
                  <p className="text-sm text-[#9CA3AF] leading-relaxed">{data.milestone.description}</p>
                )}

                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-[#6B7280] mb-1">Project</div>
                    <div className="text-sm text-white font-medium">{data.project?.name || "—"}</div>
                  </div>
                  {data.milestone?.due_date && (
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-[#6B7280] mb-1">Due date</div>
                      <div className="text-sm text-white font-medium">{fmtDate(data.milestone.due_date)}</div>
                    </div>
                  )}
                  {data.milestone?.billing_amount != null && (
                    <div className="col-span-2">
                      <div className="text-[10px] uppercase tracking-widest text-[#6B7280] mb-1">Contractual value</div>
                      <div className="text-2xl font-bold text-[#00F5D4]">
                        {fmt(data.milestone.billing_amount)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Sign-off form — only if pending */}
              {data.status === "pending" && (
                <form onSubmit={handleConfirm} className="rounded-xl border border-[#1F2937] bg-[#111827] p-6 space-y-4">
                  <h2 className="text-sm font-semibold text-white">Your approval</h2>
                  <p className="text-xs text-[#6B7280]">
                    By clicking "Sign off", you confirm that the milestone above has been
                    delivered to your satisfaction. This constitutes a legally binding digital signature.
                  </p>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-[#6B7280] mb-1.5">
                        Full name <span className="text-[#FF007A]">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={form.signer_name}
                        onChange={(e) => setForm((f) => ({ ...f, signer_name: e.target.value }))}
                        placeholder="Your full name"
                        className="w-full h-11 rounded-lg bg-[#030712] border border-[#374151] text-white text-sm px-3 placeholder:text-[#4B5563] focus:outline-none focus:border-[#00F5D4]/50 focus:shadow-glow-teal transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-[#6B7280] mb-1.5">
                        Email address <span className="text-[#FF007A]">*</span>
                      </label>
                      <input
                        type="email"
                        required
                        value={form.signer_email}
                        onChange={(e) => setForm((f) => ({ ...f, signer_email: e.target.value }))}
                        placeholder="your@email.com"
                        className="w-full h-11 rounded-lg bg-[#030712] border border-[#374151] text-white text-sm px-3 placeholder:text-[#4B5563] focus:outline-none focus:border-[#00F5D4]/50 focus:shadow-glow-teal transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] uppercase tracking-widest text-[#6B7280] mb-1.5">
                        Note (optional)
                      </label>
                      <textarea
                        rows={3}
                        value={form.signer_note}
                        onChange={(e) => setForm((f) => ({ ...f, signer_note: e.target.value }))}
                        placeholder="Any comments or conditions…"
                        className="w-full rounded-lg bg-[#030712] border border-[#374151] text-white text-sm px-3 py-2.5 placeholder:text-[#4B5563] focus:outline-none focus:border-[#00F5D4]/50 focus:shadow-glow-teal transition-all resize-none"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="text-xs text-[#FF007A] bg-[#FF007A]/8 border border-[#FF007A]/20 rounded-lg px-3 py-2">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting || !form.signer_name.trim() || !form.signer_email.trim()}
                    className="w-full h-12 rounded-lg bg-[#00F5D4] text-[#030712] text-sm font-bold tracking-wide shadow-glow-teal-md hover:bg-[#00e5c4] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Submitting…" : "Sign off on this milestone"}
                  </button>

                  <p className="text-[10px] text-[#4B5563] text-center">
                    This sign-off is timestamped and IP-logged for audit purposes.
                  </p>
                </form>
              )}
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="relative border-t border-[#1F2937] px-6 py-4 text-center">
        <span className="text-[10px] text-[#374151] font-mono tracking-widest uppercase">
          Powered by CombinedWork · Secure Document Sign-Off
        </span>
      </footer>
    </div>
  );
}
